/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useConfigStore } from '@/stores';
import type { FloorInfo, KioskSelectedCategory } from '@/services/dto/kiosk-config';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import poiUtils from '@/utils/poi';
import { getSearchMenu, resolveShopPoiContents } from '@/utils/shop';
import type { TFunction } from 'i18next';
import { sortCategories } from '@/utils/configStore';

const optionAllCategory: KioskSelectedCategory = {
    clientCode: 'all',
    name: 'common.text.all_category',
    id: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};

const sortFloorsByOrder = <T extends { order: number }>(floors: T[]): T[] => [...floors].sort((a, b) => a.order - b.order);

export const useShopBuildingOptions = (t: TFunction<'translation', undefined>) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);

    return useMemo(() => {
        const allBuildingOption = {
            label: t('shop.search.text.content.all_building'),
            value: 'all',
        };

        const listBuildings =
            buildings?.map((building) => ({
                label: poiUtils.getBuildingLocalizedName(building, language, mainLanguage),
                value: building.id,
            })) || [];

        return buildings?.length && buildings.length > 1 ? [allBuildingOption, ...listBuildings] : listBuildings;
    }, [language, mainLanguage, buildings, t]);
};

/**
 * Hook to memoize floor list computation with optimized logic
 */
export const useShopFloorList = (selectedBuildingId: string | null, t: TFunction) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);

    return useMemo(() => {
        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };

        if (!buildings) {
            return [allFloorOption];
        }

        const searchMenu = getSearchMenu(buildingMenus, language);
        const pois = resolveShopPoiContents(searchMenu, selectedBuildingId, SHOP_SEARCH_MODE.SEARCH_BY_FLOOR);
        const poiFloorIds = new Set(pois.map((poi) => poi.studioFloorId));

        // === If "all buildings" is selected ===
        if (selectedBuildingId === 'all') {
            const labelToFloorInfo = new Map<string, { codes: Set<string>; order: number }>();

            buildings.forEach((building) => {
                building.floors?.forEach((floor) => {
                    if (!poiFloorIds.has(floor.floor_code)) return;

                    const label = poiUtils.getLocalizedFloorLabel(floor, language);
                    if (!label) return;

                    const existing = labelToFloorInfo.get(label);
                    if (existing) {
                        existing.codes.add(floor.floor_code);
                        existing.order = Math.min(existing.order, floor.order ?? 0);
                    } else {
                        labelToFloorInfo.set(label, { codes: new Set([floor.floor_code]), order: floor.order ?? 0 });
                    }
                });
            });

            if (labelToFloorInfo.size === 0) return [allFloorOption];

            const mergedFloors = Array.from(labelToFloorInfo.entries()).map(([label, info]) => ({
                id: label,
                label,
                value: label,
                data: Array.from(info.codes),
                order: info.order,
            }));

            return [allFloorOption, ...sortFloorsByOrder(mergedFloors)];
        }

        // === If a specific building is selected ===
        const buildingId = selectedBuildingId || buildings[0]?.id;
        if (!buildingId) return [allFloorOption];

        const building = buildings.find((b) => b.id === buildingId);

        if (!building) return [allFloorOption];

        const floors = sortFloorsByOrder(
            (building.floors ?? [])
                .map((floor) => ({
                    id: floor.id,
                    label: poiUtils.getLocalizedFloorLabel(floor, language),
                    value: floor.id,
                    data: floor,
                    order: floor.order ?? 0,
                }))
                .filter((floor) => floor.label && poiFloorIds.has((floor.data as FloorInfo).floor_code))
        );

        return floors.length > 0 ? [allFloorOption, ...floors] : [allFloorOption];
    }, [language, buildings, selectedBuildingId, buildingMenus]);
};

export const useShopCategories = () => {
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);

    return useMemo(() => {
        const searchMenu = getSearchMenu(buildingMenus, language) ?? getSearchMenu(buildingMenus, mainLanguage);
        const selectedCategories = searchMenu?.selectedCategories ?? [];

        if (!selectedCategories.length) return [optionAllCategory];

        return sortCategories([optionAllCategory, ...selectedCategories]);
    }, [buildingMenus, language, mainLanguage]);
};
